import { Form, FormValues, Input } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import PhoneInput from '../../../../shared/components/inputs/phone-input/phone-input';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { checkIfValidatePhone, validatePhone } from '../../../../shared/utils/phone-validation/phone-validation';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useEmailSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestEmailModalId } from '../../constants/constants';
import { selectTempSignRequestId, setTempSignRequestId } from '../../store/sign-requests-slice';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';

const SignRequestEmailFormModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const [form] = Form.useForm<{ email: string; phone: string }>();
  const dispatch = useDispatch();
  const { t } = useTranslation(['sign-requests', 'common']);
  const activeSignRequestId = useAppSelector(selectTempSignRequestId);
  const [emailRequest, { isLoading }] = useEmailSignRequestMutation();
  const { data: userSettings } = useGetUserSettingsQuery();

  const closeFormModal = () => {
    dispatch(closeModal(signRequestEmailModalId));
    dispatch(setTempSignRequestId(null));
  };

  const setSignRequestEmailAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('signRequestEmailModal.success.message'),
      }),
    );
  };

  const submitForm = (formValues: FormValues<typeof form>) => {
    if (!activeSignRequestId?.length) {
      return;
    }

    emailRequest({
      id: activeSignRequestId,
      ...formValues,
    })
      .unwrap()
      .then(() => {
        closeFormModal();
        setSignRequestEmailAlertMessage(signRequestAlertId);
        setSignRequestEmailAlertMessage(requestPreviewAlertId);
      })
      .catch(error => {
        handleServerFormErrors(
          form,
          error.data?.errors || {
            email: [t('errors.unexpected', { ns: 'common' })],
          },
        );
      });
  };

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal
      title={t('signRequestEmailModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('action.send', { ns: 'common' })}
      onOk={() => form.submit()}
      formId={signRequestEmailModalId}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <Form id={signRequestEmailModalId} form={form} layout="vertical" onFinish={submitForm}>
        <Form.Item
          name="email"
          label={t('label.email', { ns: 'common' })}
          rules={[
            {
              type: 'email',
              message: t('errors.emailFormat', { ns: 'common' }),
              validateTrigger: 'blur',
            },
            { required: true, message: t('errors.required', { ns: 'common' }) },
          ]}
          normalize={value => value.trim()}
        >
          <Input placeholder={t('label.email', { ns: 'common' })} />
        </Form.Item>
        <Form.Item
          name="phone"
          label={t('label.phone', { ns: 'common' })}
          rules={[
            {
              validator: (_rule, value: string) =>
                checkIfValidatePhone(value, '') ? validatePhone(value) : Promise.resolve(),
              message: t('errors.phoneFormat', { ns: 'common' }),
            },
          ]}
        >
          <PhoneInput
            value=""
            inputLabel={t('label.phone', { ns: 'common' })}
            countryCode={userSettings?.country.toUpperCase()}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SignRequestEmailFormModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestEmailModalId} Component={SignRequestEmailFormModalContent} />;
};

export default SignRequestEmailFormModal;
